import React, { useState } from 'react';
import { 
  CreditCard, 
  Building2, 
  QrCode, 
  Wallet, 
  Lock, 
  ChevronDown, 
  CheckCircle2,
  ArrowLeft 
} from 'lucide-react';

const PaymentPage = () => {
  const [view, setView] = useState('methods'); 
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [activeWallet, setActiveWallet] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // --- Card Form States ---
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");

  const handlePayment = () => {
    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 2000);
  };

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  // --- Card Validation Handlers ---
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Only letters (Small & Capital)
    setCardName(val);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 16);
    setCardNumber(val);
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 2) {
      val = val.slice(0, 2) + "/" + val.slice(2, 4);
    }
    setExpiry(val.slice(0, 5));
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 3);
    setCvv(val);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-800 flex justify-center items-start pt-16">
      <div className="w-full max-w-5xl px-6 pb-20">
        
        <div className="mb-14 text-center">
          <h1 className="text-5xl md:text-6xl font-[900] text-[#1e2b58] tracking-tight">
            Secure Payment Checkout
          </h1>
        </div>
        
        <div className="flex flex-col lg:flex-row justify-center gap-4 items-start">
          
          {/* Left Side Container */}
          <div className="w-full max-w-md">
            
            {view === 'methods' ? (
              <div className="space-y-4 animate-in fade-in duration-300">
                <h3 className="text-lg font-bold text-slate-700 mb-2 tracking-tight">Choose Your Payment Method</h3>
                
                {/* UPI Box */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex items-center justify-between hover:border-[#1e2b58] transition-all cursor-pointer">
                  <div className="flex gap-4">
                    <QrCode size={26} className="text-slate-400 mt-1" />
                    <div>
                      <p className="font-bold text-base">UPI / QR Code</p>
                      <p className="text-xs text-gray-400 max-w-[140px]">Scan & Pay instantly</p>
                    </div>
                  </div>
                  <div className="bg-white border-2 border-slate-100 p-1 rounded-lg shadow-sm">
                    <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=B2BBhaarat" alt="QR" className="w-14 h-14 object-contain" />
                  </div>
                </div>

                {/* Credit Card Box */}
                <div 
                  onClick={() => setView('card-details')}
                  className="p-5 rounded-xl border-2 border-[#1e2b58]/10 bg-white shadow-sm flex items-center justify-between cursor-pointer hover:border-[#1e2b58] transition-all"
                >
                  <div className="flex items-center gap-4">
                    <CheckCircle2 size={24} className="text-[#1e2b58]" />
                    <div>
                      <p className="font-bold text-sm text-[#1e2b58]">Credit / Debit Card</p>
                      <p className="text-[10px] text-gray-400 font-medium">All Indian Banks</p>
                    </div>
                  </div>
                  <div className="flex gap-3 items-center bg-gray-50/80 px-2 py-1.5 rounded-lg">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5e/Visa_Inc._logo.svg/2560px-Visa_Inc._logo.svg.png" className="h-2.5 object-contain" alt="visa" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/MasterCard_Logo.svg/2560px-MasterCard_Logo.svg.png" className="h-5 object-contain" alt="mastercard" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/cb/Rupay-Logo.png/1200px-Rupay-Logo.png" className="h-5 object-contain" alt="rupay" />
                  </div>
                </div>

                {/* NEFT / RTGS Details */}
                <div className="group">
                  <div 
                    onClick={() => toggleSection('neft')}
                    className={`p-5 rounded-xl border bg-white shadow-sm flex items-center justify-between cursor-pointer transition-all ${openSection === 'neft' ? 'border-[#1e2b58]' : 'border-gray-100 hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <Building2 size={24} className={openSection === 'neft' ? 'text-[#1e2b58]' : 'text-slate-500'} />
                      <p className={`font-bold text-sm ${openSection === 'neft' ? 'text-[#1e2b58]' : 'text-slate-500'}`}>NEFT / RTGS Details</p>
                    </div>
                    <ChevronDown size={18} className={`transition-transform duration-300 ${openSection === 'neft' ? 'rotate-180 text-[#1e2b58]' : 'text-slate-400'}`} />
                  </div>
                  
                  {openSection === 'neft' && (
                    <div className="mt-2 p-5 bg-[#f0f7ff] rounded-xl border border-blue-100 space-y-3 animate-in slide-in-from-top-2 duration-300">
                      <div className="grid grid-cols-2 gap-y-3">
                        <div><p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">Beneficiary Name</p><p className="text-xs font-bold text-[#1e2b58]">TRADY FROG SERVICES</p></div>
                        <div><p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">Bank Name</p><p className="text-xs font-bold text-[#1e2b58]">ICICI BANK LTD</p></div>
                        <div><p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">Account Number</p><p className="text-xs font-bold text-[#1e2b58]">987654321012</p></div>
                        <div><p className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">IFSC Code</p><p className="text-xs font-bold text-[#1e2b58]">ICIC0001234</p></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Digital Wallets */}
                <div className="group">
                  <div 
                    onClick={() => toggleSection('wallet')}
                    className={`p-5 rounded-xl border bg-white shadow-sm flex items-center justify-between cursor-pointer transition-all ${openSection === 'wallet' ? 'border-[#1e2b58]' : 'border-gray-100 hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-4">
                      <Wallet size={24} className={openSection === 'wallet' ? 'text-[#1e2b58]' : 'text-slate-500'} />
                      <p className={`font-bold text-sm ${openSection === 'wallet' ? 'text-[#1e2b58]' : 'text-slate-500'}`}>Digital Wallets</p>
                    </div>
                    <ChevronDown size={18} className={`transition-transform duration-300 ${openSection === 'wallet' ? 'rotate-180 text-[#1e2b58]' : 'text-slate-400'}`} />
                  </div>

                  {openSection === 'wallet' && (
                    <div className="mt-2 p-4 bg-white rounded-xl border border-gray-100 shadow-inner space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="grid grid-cols-3 gap-3">
                        <div onClick={() => setActiveWallet('Paytm')} className={`flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all ${activeWallet === 'Paytm' ? 'bg-slate-100 border border-[#1e2b58]' : 'hover:bg-slate-50'}`}>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/2/24/Paytm_Logo_%28standalone%29.svg" className="h-4 object-contain mb-1" alt="paytm" />
                          <span className="text-[9px] font-bold text-slate-500 uppercase">Paytm</span>
                        </div>
                        <div onClick={() => setActiveWallet('PhonePe')} className={`flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all ${activeWallet === 'PhonePe' ? 'bg-slate-100 border border-[#1e2b58]' : 'hover:bg-slate-50'}`}>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/7/71/PhonePe_Logo.svg" className="h-4 object-contain mb-1" alt="phonepe" />
                          <span className="text-[9px] font-bold text-slate-500 uppercase">PhonePe</span>
                        </div>
                        <div onClick={() => setActiveWallet('Amazon')} className={`flex flex-col items-center p-2 rounded-lg cursor-pointer transition-all ${activeWallet === 'Amazon' ? 'bg-slate-100 border border-[#1e2b58]' : 'hover:bg-slate-50'}`}>
                          <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" className="h-4 object-contain mb-1" alt="amazonpay" />
                          <span className="text-[9px] font-bold text-slate-500 uppercase">Amazon</span>
                        </div>
                      </div>

                      {activeWallet && (
                        <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 animate-in fade-in duration-300">
                          <label className="text-[10px] font-bold text-black uppercase tracking-widest block mb-2">Enter Linked {activeWallet} Mobile Number</label>
                          <input 
                            type="tel" 
                            maxLength={10}
                            onChange={(e) => { e.target.value = e.target.value.replace(/\D/g, '') }}
                            placeholder="XXXXX XXXXX" 
                            className="w-full p-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#1e2b58] font-bold text-black" 
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* CARD DETAILS VIEW */
              <div className="bg-white rounded-3xl border border-gray-200 shadow-md p-8 animate-in slide-in-from-right duration-300">
                <button onClick={() => setView('methods')} className="flex items-center gap-2 text-slate-400 hover:text-[#1e2b58] mb-6 transition-colors">
                  <ArrowLeft size={18} />
                  <span className="text-sm font-bold uppercase tracking-wider text-black font-black">Back</span>
                </button>
                <h3 className="text-2xl font-black text-[#1e2b58] mb-8">Card Details</h3>
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-bold text-black uppercase tracking-widest block mb-2">Card Holder Name</label>
                    <input type="text" value={cardName} onChange={handleNameChange} placeholder="Name on Card" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:border-[#1e2b58] font-bold text-black placeholder:text-slate-300" />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-black uppercase tracking-widest block mb-2">Card Number</label>
                    <input type="text" value={cardNumber} onChange={handleCardNumberChange} placeholder="0000 0000 0000 0000" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:border-[#1e2b58] font-mono text-black placeholder:text-slate-300" />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-[10px] font-bold text-black uppercase tracking-widest block mb-2">Expiry</label>
                      <input type="text" value={expiry} onChange={handleExpiryChange} placeholder="MM/YY" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:border-[#1e2b58] text-black placeholder:text-slate-300" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[10px] font-bold text-black uppercase tracking-widest block mb-2">CVV</label>
                      <input type="password" value={cvv} onChange={handleCvvChange} placeholder="***" className="w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:outline-none focus:border-[#1e2b58] text-black placeholder:text-slate-300" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Side: Order Summary */}
          <div className="w-full max-sm flex flex-col shadow-lg rounded-3xl overflow-hidden border border-slate-200 lg:-mt-8 max-w-sm">
            <div className="bg-[#f0f7ff] p-7 border-b border-blue-100">
              <h3 className="text-xl font-bold text-[#1e2b58] mb-6">Order Summary</h3>
              <div className="space-y-5 text-sm text-slate-600 font-medium">
                <div className="flex justify-between"><span>Service Fee</span><span className="text-slate-400 italic font-normal">Subtotal</span></div>
                <div className="flex justify-between text-slate-800 font-bold"><span>Platform Charge</span><span>2,003</span></div>
                <div className="flex justify-between text-slate-800 font-bold"><span>GST (18%)</span><span>3,126</span></div>
              </div>
            </div>
            <div className="bg-white p-7">
              <p className="text-slate-500 text-xs font-bold mb-2 uppercase tracking-wider">Total Amount Due:</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-3xl font-[900] text-slate-900">₹ 45,678.00</span>
                <span className="text-lg font-bold text-[#e31e24]">INR</span>
              </div>
              <p className="text-[10px] text-gray-400 font-mono uppercase border-t pt-4 border-slate-100">ORDER ID: 0000 0000 0008 5581</p>
              <button onClick={handlePayment} disabled={isProcessing} className={`w-full text-white mt-8 py-4 rounded-xl font-black text-lg shadow-xl transition-all active:scale-[0.98] ${isProcessing ? 'bg-gray-400' : 'bg-[#1e2b58] hover:bg-[#e31e24]'}`}>
                {isProcessing ? 'Processing...' : 'Confirm & Pay'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;